import { type DocumentMeta } from "@prismicio/editor-fields"
import {
  BlankSlate,
  BlankSlateActions,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardHeaderTitle,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  FileDropZone,
  FileUploadButton,
  Text,
} from "@prismicio/editor-ui"
import { useEffect, useState } from "react"

import {
  abmLandingPagesApiClient,
  type LandingPagesApiClient,
  productLandingPagesApiClient,
} from "../../../app/services/aiLandingPage"
import { GenerateLandingPagesState } from "./GenerateLandingPagesState"
import { useGenerateLandingPages } from "./useGenerateLandingPages"

export type GenerateLandingPagesDialogVariant =
  | "generateProductLandingPages"
  | "personalizeLandingPageForABM"

type CsvFile = {
  filename: string
  content: string
}

const TITLES: Record<GenerateLandingPagesDialogVariant, string> = {
  generateProductLandingPages: "Generate Product Landing Pages",
  personalizeLandingPageForABM: "Personalize Landing Page for ABM",
}

const API_CLIENTS: Record<
  GenerateLandingPagesDialogVariant,
  LandingPagesApiClient
> = {
  generateProductLandingPages: productLandingPagesApiClient,
  personalizeLandingPageForABM: abmLandingPagesApiClient,
}

type GenerateLandingPagesDialogProps = {
  document: DocumentMeta
  variant: GenerateLandingPagesDialogVariant
  onCloseRequest: (taskState: GenerateLandingPagesState) => void
}

export function GenerateLandingPagesDialog(
  props: GenerateLandingPagesDialogProps,
) {
  const { document, variant, onCloseRequest } = props
  const { title } = document

  const { action, disabled, taskState } = useGenerateLandingPages({
    apiClient: API_CLIENTS[variant],
  })

  const [csv, setCSV] = useState<CsvFile>()

  function onOpenChange(open: boolean) {
    if (!open) onCloseRequest(taskState)
  }

  useEffect(() => {
    if (taskState.status === "SUCCEEDED") {
      onCloseRequest(taskState)
    }
  }, [taskState, onCloseRequest])

  async function onFilesSelected(files: File[]) {
    const firstFile = files[0]
    if (!firstFile) return

    // TODO: Show an error and allow re-upload.
    if (!firstFile.type.startsWith("text/")) return

    setCSV(await readFile(firstFile))

    function readFile(file: File): Promise<CsvFile> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () =>
          resolve({
            filename: file.name,
            content: reader.result as string,
          })
        reader.onerror = reject
        reader.readAsText(file)
      })
    }
  }

  async function onGenerateLandingPages() {
    if (!csv) return
    await action({ document, csv: csv.content })
  }

  return (
    <Dialog open onOpenChange={onOpenChange} noDescription>
      <DialogHeader icon="autoFixHigh" title={TITLES[variant]} />
      <DialogContent>
        <Box
          overflowY="auto"
          flexGrow={1}
          flexDirection="column"
          padding={24}
          gap={16}
        >
          <Text>
            Generate landing pages based on <Text variant="bold">{title}</Text>.
          </Text>

          <Card color="grey2" variant="dashed" paddingBlock={12} radius={4}>
            <FileDropZone
              assetType="document"
              maxFiles={1}
              onFilesSelected={onFilesSelected}
              overlay={
                <Box
                  justifyContent="center"
                  flexDirection="column"
                  height="100%"
                  padding={8}
                >
                  <BlankSlate>
                    <BlankSlateIcon
                      name="cloudUpload"
                      lineColor="purple10"
                      backgroundColor="purple3"
                      borderColor="purple6"
                      borderStyle="dashed"
                      size="small"
                    />
                    <BlankSlateTitle size="regular">
                      Drop file to instantly upload
                    </BlankSlateTitle>
                  </BlankSlate>
                </Box>
              }
            >
              <CardHeader sx={{ flexWrap: "wrap" }}>
                <CardHeaderTitle>
                  <Text variant="bold" noWrap>
                    CSV File
                  </Text>
                </CardHeaderTitle>
              </CardHeader>
              <CardContent>
                <Card
                  color="grey2"
                  variant="dashed"
                  paddingBlock={12}
                  radius={4}
                  sx={{ width: "100%" }}
                >
                  <CardContent>
                    <BlankSlate variant="horizontal">
                      <BlankSlateIcon
                        name="cloudUpload"
                        lineColor="purple9"
                        backgroundColor="purple4"
                        size="small"
                      />
                      <BlankSlateTitle>
                        {csv
                          ? `${csv.filename} selected`
                          : "You haven't selected a file"}
                      </BlankSlateTitle>
                      <BlankSlateActions>
                        <FileUploadButton
                          onFilesSelected={onFilesSelected}
                          renderButton={(onClick) => (
                            <Box display="inline-flex" as="span">
                              <Button
                                disabled={disabled}
                                onClick={onClick}
                                color="grey"
                              >
                                Upload file
                              </Button>
                            </Box>
                          )}
                        />
                      </BlankSlateActions>
                    </BlankSlate>
                  </CardContent>
                </Card>
              </CardContent>
            </FileDropZone>
          </Card>
        </Box>
        <DialogActions>
          <DialogCancelButton disabled={disabled} />
          <DialogActionButton
            disabled={disabled || !csv}
            loading={taskState?.status === "RUNNING"}
            onClick={onGenerateLandingPages}
          >
            Generate landing pages
          </DialogActionButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}
